import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./UserProfile.scss";
import { Link } from "react-router-dom";
import {
  suspendUser,
  revokeVerification,
  restoreAccess,
} from "../../../services/userService";
import { getUserById } from "../../../services/userService";

function UserProfile() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const userId = location.state?.userId || searchParams.get("id");

  const [duration, setDuration] = useState("");
  const [reason, setReason] = useState("");
  const [revokeSubject, setRevokeSubject] = useState("");
  const [revokeMessage, setRevokeMessage] = useState("");
  const [restoreMessage, setRestoreMessage] = useState("");
  const modalRef = useRef(null);
  const revokeModalRef = useRef(null);
  const restoreAccessModalRef = useRef(null);

  const revokeReasons = [
    "False Ownership",
    "Duplicate Claim",
    "Invalid Documentation",
    "Fraudulent Activity",
    "Other",
  ];

  // Get user data from location state or fetch from API
  const {
    data: userData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId && !location.state?.userData,
    retry: 1,
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to load user data");
    },
  });

  // Use data from state or API
  const user = location.state?.userData || userData?.data || userData;

  // Revoke verification mutation
  const revokeMutation = useMutation({
    mutationFn: ({ userId, revokeData }) =>
      revokeVerification(userId, revokeData),
    onSuccess: (data) => {
      toast.success(data?.message || "Verification revoked successfully");

      // Reset form fields
      setRevokeSubject("");
      setRevokeMessage("");

      // Close modal using Bootstrap API
      if (revokeModalRef.current) {
        const modalElement = revokeModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Refetch user data and users list
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to revoke verification";
      toast.error(errorMessage);
    },
  });

  // Suspend user mutation
  const suspendMutation = useMutation({
    mutationFn: ({ userId, suspendData }) => suspendUser(userId, suspendData),
    onSuccess: (data) => {
      toast.success(data?.message || "User suspended successfully");

      // Close modal using Bootstrap API
      if (modalRef.current) {
        const modalElement = modalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          // Fallback: use jQuery/bootstrap if available
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Refetch user data and users list
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to suspend user";
      toast.error(errorMessage);
    },
  });

  const handleSuspend = () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    // Validate duration
    const durationNum = Number(duration);
    if (!duration || isNaN(durationNum) || durationNum <= 0) {
      toast.error("Please enter a valid duration (number of days)");
      return;
    }

    if (!reason.trim()) {
      toast.error("Please enter a reason for suspension");
      return;
    }

    suspendMutation.mutate({
      userId,
      suspendData: {
        duration: durationNum,
        reason: reason.trim(),
      },
    });
  };

  // Restore access mutation
  const restoreAccessMutation = useMutation({
    mutationFn: ({ userId, restoreData }) => restoreAccess(userId, restoreData),
    onSuccess: (data) => {
      toast.success(data?.message || "Access restored successfully");

      // Reset form fields
      setRestoreMessage("");

      // Close modal using Bootstrap API
      if (restoreAccessModalRef.current) {
        const modalElement = restoreAccessModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Refetch user data and users list
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to restore access";
      toast.error(errorMessage);
    },
  });

  const handleRestoreAccess = () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    if (!restoreMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    restoreAccessMutation.mutate({
      userId,
      restoreData: {
        message: restoreMessage.trim(),
      },
    });
  };

  // Transform user data to match the display structure
  const profileData = useMemo(() => {
    if (!user) return null;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return (
          date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }) +
          " | " +
          date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        );
      } catch (e) {
        return dateString;
      }
    };

    const kycStatus =
      user.kycVerification?.charAt(0).toUpperCase() +
        user.kycVerification?.slice(1) || "Unverified";

    // Build sections array
    const sections = [
      {
        title: "Basic Info",
        items: [
          {
            label: "Name",
            value:
              user.name ||
              user.fullName ||
              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
              "N/A",
          },
          { label: "Email", value: user.email || "N/A" },
          { label: "Joined", value: formatDate(user.createdAt) },
          { label: "Country", value: user.country || "N/A" },
          {
            label: "Role",
            value:
              user.userType === "business" ? "Business User" : "Regular User",
          },
        ],
      },
      {
        title: "Verification (KYC)",
        items: [
          {
            label: "Status",
            value: kycStatus,
            type:
              user.kycVerification === "verified"
                ? "status-approved"
                : "status",
          },
          { label: "ID Type", value: user.idType || "N/A" },
          {
            label: "Verified On",
            value: user.verifiedAt ? formatDate(user.verifiedAt) : "N/A",
          },
        ],
      },
    ];

    // Only add Plates Owned section for regular users (not business users)
    if (
      user.userType === "user" ||
      !user.userType ||
      user.userType !== "business"
    ) {
      // Map license plates if available
      const plates = (user.licensePlateImageUrls || []).map((url, index) => ({
        label: `Plate ${index + 1}`,
        value: "Verified",
        type: "status-approved",
        image: url,
        itemType: "plate-card",
      }));

      sections.push({
        title: "Plates Owned",
        items:
          plates.length > 0
            ? plates
            : [
                {
                  label: "No Plates",
                  value: "No plates found",
                  type: "status",
                  image: process.env.PUBLIC_URL + "/images/plate.svg",
                  itemType: "plate-card",
                },
              ],
      });
    }

    // Add Reports Summary section
    sections.push({
      title: "Reports Summary",
      items: [
        { label: "Active Reports", value: user.activeReports || "0" },
        { label: "Resolved Reports", value: user.resolvedReports || "0" },
        { label: "Pending", value: user.pendingReports || "0" },
      ],
    });

    return { sections };
  }, [user]);

  const handleRevoke = () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    if (!revokeSubject.trim()) {
      toast.error("Please select a reason");
      return;
    }

    if (!revokeMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    revokeMutation.mutate({
      userId,
      revokeData: {
        subject: revokeSubject.trim(),
        message: revokeMessage.trim(),
      },
    });
  };

  const handleReasonChange = (reason) => {
    setRevokeSubject(reason);
  };

  // Reset forms when modals are closed
  useEffect(() => {
    const suspendModalElement = modalRef.current;
    const revokeModalElement = revokeModalRef.current;
    const restoreAccessModalElement = restoreAccessModalRef.current;

    const handleSuspendModalHidden = () => {
      setDuration("");
      setReason("");
    };

    const handleRevokeModalHidden = () => {
      setRevokeSubject("");
      setRevokeMessage("");
    };

    const handleRestoreAccessModalHidden = () => {
      setRestoreMessage("");
    };

    if (suspendModalElement) {
      suspendModalElement.addEventListener(
        "hidden.bs.modal",
        handleSuspendModalHidden,
      );
    }

    if (revokeModalElement) {
      revokeModalElement.addEventListener(
        "hidden.bs.modal",
        handleRevokeModalHidden,
      );
    }

    if (restoreAccessModalElement) {
      restoreAccessModalElement.addEventListener(
        "hidden.bs.modal",
        handleRestoreAccessModalHidden,
      );
    }

    return () => {
      if (suspendModalElement) {
        suspendModalElement.removeEventListener(
          "hidden.bs.modal",
          handleSuspendModalHidden,
        );
      }
      if (revokeModalElement) {
        revokeModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRevokeModalHidden,
        );
      }
      if (restoreAccessModalElement) {
        restoreAccessModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRestoreAccessModalHidden,
        );
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <p>Loading user data...</p>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <p>Error loading user data. Please try again.</p>
        <Link to="/user-management">Back to User Management</Link>
      </div>
    );
  }

  const displayName =
    user.name ||
    user.fullName ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.email ||
    "User";
  const userCode = `U${user._id?.slice(-4) || "0000"}`;

  return (
    <React.Fragment>
      <header className="reported-plate-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/user-management">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">User Detail</h4>
        </div>

        {/* <div className="right-side">
          <button
            className="revoke"
            data-bs-toggle="modal"
            data-bs-target="#revokeVerifyModal"
          >
            Revoke Verification
          </button>
          <button
            className="info"
            data-bs-toggle="modal"
            data-bs-target="#suspendModal"
          >
            Suspend User
          </button>
          <button
            className="suspend"
            data-bs-toggle="modal"
            data-bs-target="#restoreAccessModal"
          >
            Restore Access
          </button>
        </div> */}
      </header>
      <div className="report-id-wrapper">
        <div className="flex align-items-center justify-content-between">
          <p className="report-id-name">User Profile</p>
          <p className="report-id">
            {displayName} ({userCode})
          </p>
        </div>
      </div>
      <div className="user-profile">
        <div className="row">
          {profileData?.sections.map((section, index) => {
            let colSize = "12"; // default column size

            // Adjust columns for specific sections if needed
            if (section.title === "Plates Owned") colSize = "12";

            return (
              <div key={index} className={`col-lg-12 col-xl-${colSize}`}>
                <div className="card">
                  <h4>{section.title}</h4>
                  <div className="inner-content">
                    <div className="row">
                      {section.items.map((item, i) => {
                        // Check if the item is a plate card
                        if (item.itemType === "plate-card") {
                          return (
                            <div key={i} className="col-lg-4 col-xl-4 col-md-6">
                              <div className="plate-card">
                                <div className="plate-image">
                                  <img src={item.image} alt={item.label} />
                                </div>
                                <div className="plate-info">
                                  <h5>{item.label}</h5>
                                  <p
                                    className={`status-badge ${
                                      item.type === "status-approved"
                                        ? "approved"
                                        : "pending"
                                    }`}
                                  >
                                    <span className="dot"></span> {item.value}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Default item rendering
                        return (
                          <div key={i} className="col-lg-4 col-xl-4 col-md-6">
                            <div className="content-wrapper">
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/images/user-icon.svg"
                                }
                                alt="icon"
                                className="icon"
                              />
                              <div className="content">
                                <h5>{item.label}</h5>
                                {item.type === "status" && (
                                  <p className="status-badge pending">
                                    <span className="dot"></span> {item.value}
                                  </p>
                                )}
                                {item.type === "status-approved" && (
                                  <p className="status-badge approved">
                                    <span className="dot"></span> {item.value}
                                  </p>
                                )}
                                {!item.type && <p>{item.value}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revoke Modal */}
      <div
        ref={revokeModalRef}
        className="modal fade"
        id="revokeVerifyModal"
        tabIndex="-1"
        aria-labelledby="revokeVerifyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="revokeVerifyModalLabel">
                Revoke Verification
              </h5>
            </div>

            <div className="modal-body">
              <h3>
                Are you sure you want to remove {displayName}'s verified (blue
                tick) badge?
              </h3>
              <label className="form-label">
                This will reset their KYC status to unverified
              </label>
              <label className="form-label">Select Reason</label>

              {/* Radio buttons - only one selectable */}
              {revokeReasons.map((reasonOption, index) => (
                <div key={index} className="form-check mb-2">
                  <input
                    type="radio"
                    name="revokeReason"
                    id={`reason${index + 1}`}
                    checked={revokeSubject === reasonOption}
                    onChange={() => handleReasonChange(reasonOption)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`reason${index + 1}`}
                  >
                    {reasonOption}
                  </label>
                </div>
              ))}

              {/* Textarea */}
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={revokeMessage}
                onChange={(e) => setRevokeMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={revokeMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="red-btn"
                onClick={handleRevoke}
                disabled={revokeMutation.isPending}
              >
                {revokeMutation.isPending
                  ? "Revoking..."
                  : "Revoke Verification"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Suspend Modal */}
      <div
        ref={modalRef}
        className="modal fade"
        id="suspendModal"
        tabIndex="-1"
        aria-labelledby="suspendModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="suspendModalLabel">
                Suspend User
              </h5>
            </div>

            <div className="modal-body">
              <h3>Hide this plate and the associated user profile?</h3>
              <label className="form-label">
                It will not be visible to others during review.
              </label>
              <br />
              <label className="form-label">Duration (Days)</label>
              <input
                type="number"
                className="form-control"
                placeholder="30"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                required
              />
              <label className="form-label">Reason for Suspension</label>

              <textarea
                className="form-control"
                rows="3"
                placeholder="Write here"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={suspendMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="info-btn"
                onClick={handleSuspend}
                disabled={suspendMutation.isPending}
              >
                {suspendMutation.isPending ? "Suspending..." : "Suspend User"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Restore Access Modal */}
      <div
        ref={restoreAccessModalRef}
        className="modal fade"
        id="restoreAccessModal"
        tabIndex="-1"
        aria-labelledby="restoreAccessModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="restoreAccessModalLabel">
                Restore Access
              </h5>
            </div>

            <div className="modal-body">
              <h3>Restore access for {displayName}?</h3>
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Write here"
                value={restoreMessage}
                onChange={(e) => setRestoreMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={restoreAccessMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="submit-btn"
                onClick={handleRestoreAccess}
                disabled={restoreAccessMutation.isPending}
              >
                {restoreAccessMutation.isPending
                  ? "Restoring..."
                  : "Confirm Restoration"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default UserProfile;
